import { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Divider,
  Drawer,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  ThemeProvider,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import DrawerHeader from "shared-ui/src/components/detail-drawer/DrawerHeader";
import { useGetAccountProfileDrawerProfile } from "@features/unified-user-header-feature/hooks/useGetAccountProfileDrawerProfile";
import CompanyProfileTab from "@features/unified-user-header-feature/components/profile-drawers/CompanyProfileTab";
import ShiAccountTeamTab from "@features/unified-user-header-feature/components/profile-drawers/ShiAccountTeamTab";
import { AccountProfileShiTeamDto } from "@lib/ShiOneClient";
import { useThemeMode } from "../../../../../../theme/ThemeModeProviderContext";
import lightTheme from "shared-ui/src/theme";
import darkTheme from "shared-ui/src/theme/darkTheme";

interface CompanyProfileDrawerProps {
  open: boolean;
  onClose: () => void;
  companyLogoUrl?: string;
}

function LoadingState() {
  return (
    <Stack spacing={2} sx={{ p: 3 }}>
      <Skeleton width="45%" />
      <Skeleton width="70%" />
      <Skeleton variant="rounded" height={96} />
      <Skeleton variant="rounded" height={180} />
    </Stack>
  );
}

export default function CompanyProfileDrawer({ open, onClose, companyLogoUrl }: Readonly<CompanyProfileDrawerProps>) {
  const [tab, setTab] = useState(0);
  const { data, isLoading, isError, error } = useGetAccountProfileDrawerProfile(open);
  const { mode } = useThemeMode();
  const drawerTheme = mode === "light" ? lightTheme : darkTheme;

  useEffect(() => {
    if (!open) setTab(0);
  }, [open]);

  return (
    <ThemeProvider theme={drawerTheme}>
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      role="dialog"
      aria-label="Company Profile"
      slotProps={{
        paper: {
          sx: {
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            width: { xs: "100vw", sm: "40vw" },
            minWidth: { sm: 360 },
            maxWidth: "100vw",
            overflow: "hidden",
          },
        },
      }}
    >
      <DrawerHeader
        iconElement={
          <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
            <BusinessIcon fontSize="small" />
          </Avatar>
        }
        header={data?.accountName ?? "Company Profile"}
        subheader="Organization"
        onClose={onClose}
      />
      <Divider />
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, width: "100%", overflow: "hidden" }}>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          aria-label="Company profile drawer tabs"
          indicatorColor="secondary"
          textColor="secondary"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            flexShrink: 0,
            px: 1,
            "& .MuiTab-root": {
              minHeight: 48,
              textTransform: "uppercase",
              fontWeight: 600,
            },
          }}
        >
          <Tab
            id="company-profile-tab-details"
            value={0}
            aria-controls="company-profile-panel-details"
            label="Profile"
          />
          <Tab
            id="company-profile-tab-team"
            value={1}
            aria-controls="company-profile-panel-team"
            label="SHI Account Team"
          />
        </Tabs>
        <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: "auto", bgcolor: "table.header", width: "100%" }}>
          {isLoading && <LoadingState />}
          {isError && (
            <Alert severity="error" sx={{ m: 3 }}>
              {error?.message ?? "Unable to load account profile."}
            </Alert>
          )}
          {!isLoading && !isError && data && (
            <>
              <Box
                id="company-profile-panel-details"
                role="tabpanel"
                aria-labelledby="company-profile-tab-details"
                hidden={tab !== 0}
              >
                {tab === 0 && <CompanyProfileTab account={data} companyLogoUrl={companyLogoUrl} />}
              </Box>
              <Box
                id="company-profile-panel-team"
                role="tabpanel"
                aria-labelledby="company-profile-tab-team"
                hidden={tab !== 1}
              >
                {tab === 1 && (
                  <ShiAccountTeamTab team={data.shiAccountTeam ?? new AccountProfileShiTeamDto()} />
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
    </ThemeProvider>
  );
}
